import Text from '@atoms/Text/Text';
import { ICountdown, ICountDownItem } from '@models/ICountdown';
import { useEffect, useState } from 'react';

const CountdownItem = ({
  time,
  singularLabel,
  pluralLabel,
  hideOnZero,
}: ICountDownItem) => {
  if (time <= 0 && hideOnZero) return null;

  return (
    <div aria-hidden="true">
      <Text as="span" variant="heading-21" className="tw-pr-1">
        {time.toString()}
      </Text>
      <Text variant="label-12" as="span">
        {getTimeLabel(time, singularLabel, pluralLabel)}
      </Text>
    </div>
  );
};

const Countdown = ({
  expiryDate,
  dayLabelPlural,
  dayLabelSingular,
  hourLabelPlural,
  hourLabelSingular,
  minuteLabelPlural,
  minuteLabelSingular,
  secondLabelPlural,
  secondLabelSingular,
}: ICountdown) => {
  const expiryTime = new Date(expiryDate).getTime();
  const [timeLeft, setTimeLeft] = useState(0);

  const calculateTimeLeft = (interval?: NodeJS.Timeout) => {
    const currentDateTime = new Date().getTime();
    let remainingTime = expiryTime - currentDateTime;

    if (remainingTime <= 0) {
      remainingTime = 0;
      clearInterval?.(interval);
    }

    setTimeLeft(remainingTime);
  };

  useEffect(() => {
    calculateTimeLeft();

    const countdownInterval = setInterval(() => {
      calculateTimeLeft(countdownInterval);
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  if (timeLeft <= 0) return null;

  const seconds = Math.floor((timeLeft / 1000) % 60);
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));

  return (
    <div className="tw-relative tw-flex tw-flex-wrap tw-gap-2 tw-text-white">
      <CountdownItem
        hideOnZero
        time={days}
        singularLabel={dayLabelSingular}
        pluralLabel={dayLabelPlural}
      />
      <CountdownItem
        hideOnZero
        time={hours}
        singularLabel={hourLabelSingular}
        pluralLabel={hourLabelPlural}
      />
      <CountdownItem
        hideOnZero
        time={minutes}
        singularLabel={minuteLabelSingular}
        pluralLabel={minuteLabelPlural}
      />
      <CountdownItem
        hideOnZero={false}
        time={seconds}
        singularLabel={secondLabelSingular}
        pluralLabel={secondLabelPlural}
      />
      <div className="tw-sr-only tw-absolute tw-inset-0" role="timer">
        {`${days} ${getTimeLabel(days, dayLabelSingular, dayLabelPlural)},
        ${hours} ${getTimeLabel(hours, hourLabelSingular, hourLabelPlural)},
        ${minutes} ${getTimeLabel(
          minutes,
          minuteLabelSingular,
          minuteLabelPlural
        )},
        ${seconds} ${getTimeLabel(
          seconds,
          secondLabelSingular,
          secondLabelPlural
        )}`}
      </div>
    </div>
  );
};

const getTimeLabel = (
  time: number,
  singularLabel: string,
  pluralLabel: string
) => (time === 1 ? singularLabel : pluralLabel);

export default Countdown;
